import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import ReactGA from 'react-ga4';
import Clarity from '@microsoft/clarity';
import axios from 'axios';

const getBackendUrl = () => {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocalhost ? 'http://localhost:8000' : 'https://gradi.ai';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Navbar = ({ currentPage, onPageChange, user, onLogout, onShowLogin, onSearchVideoSelect, onSearchChannelSelect, onOpenSearchPage, isAdmin = false }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLaunchingSearchPage, setIsLaunchingSearchPage] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const profileDropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);
  const suggestionsRef = useRef(null);

  const menuItems = [
    { id: 'home', label: 'Analyze' },
    { id: 'comparison', label: 'Compare' },
    { id: 'comments-analysis', label: 'Comments' },
    { id: 'channel-analysis', label: 'Channel' },
    { id: 'mind-map', label: 'Visual Map' },
    { id: 're-seo', label: 'Re-SEO' },
    { id: 'about', label: 'About' },
    { id: 'leaderboard', label: 'Rankings' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
      // Check if click is outside search input and results
      if (searchInputRef.current && searchResultsRef.current &&
          !searchInputRef.current.contains(event.target) &&
          !searchResultsRef.current.contains(event.target) &&
          suggestionsRef.current &&
          !suggestionsRef.current.contains(event.target)) {
        setShowSearchResults(false);
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch search suggestions as user types (YouTube-style autocomplete)
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await axios.get(
        `${API}/search-youtube-suggestions`,
        {
          params: {
            query: query.trim(),
            max_results: 6
          },
          timeout: 15000
        }
      );

      if (response.data && response.data.success) {
        setSearchSuggestions(response.data.suggestions || []);
        setShowSuggestions(true);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Suggestions error:', error);
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  // Debounced suggestions
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2 && !showSearchResults) {
        fetchSuggestions(searchQuery);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, fetchSuggestions, showSearchResults]);

  // Handle full search (when Enter is pressed or "Show results" is clicked)
  const handleSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);
    setShowSuggestions(false);

    try {
      const response = await apiService.searchYouTube(query.trim(), 5);
      if (response.success) {
        setSearchResults(response.results);
      } else {
        setSearchResults(null);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search - triggers live search as user types
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2 && !showSuggestions) {
        // Only trigger full search if not showing suggestions
        // Full search happens on Enter key or "Show results" button
      } else {
        setSearchResults(null);
        setShowSearchResults(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, showSuggestions]);

  // Show dropdown when there are results or when searching
  useEffect(() => {
    if (searchResults || (searchQuery.trim().length >= 2 && isSearching)) {
      setShowSearchResults(true);
    }
  }, [searchResults, searchQuery, isSearching]);

  const handleFullSearchClick = async () => {
    if (isLaunchingSearchPage) return;
    const trimmedQuery = searchQuery.trim();

    // If the query is too short, just open the search page so the user can type there
    if (trimmedQuery.length < 2) {
      if (onOpenSearchPage) {
        onOpenSearchPage({ query: '', results: null });
      }
      if (onPageChange) {
        onPageChange('search');
      }
      return;
    }

    try {
      setIsLaunchingSearchPage(true);
      const response = await apiService.searchYouTube(trimmedQuery, 30);
      setShowSearchResults(false);
      setShowSuggestions(false);
      if (onOpenSearchPage) {
        onOpenSearchPage({ query: trimmedQuery, results: response.results });
      }
      if (onPageChange) {
        onPageChange('search');
      }
    } catch (error) {
      console.error('Full search error:', error);
    } finally {
      setIsLaunchingSearchPage(false);
    }
  };

  // Handle search result click
  const handleSearchResultClick = (result) => {
    if (!result || !result.type || !result.id) {
      console.error('Invalid search result:', result);
      return;
    }

    if (result.type === 'video') {
      if (!result.id || result.id === 'null' || result.id === 'undefined') {
        console.error('Invalid video ID:', result.id);
        return;
      }
      const url = `https://www.youtube.com/watch?v=${result.id}`;
      if (onSearchVideoSelect) {
        onSearchVideoSelect(url);
      }
      if (onPageChange) {
        onPageChange('home');
      }
    } else if (result.type === 'channel') {
      if (!result.id || result.id === 'null' || result.id === 'undefined') {
        console.error('Invalid channel ID:', result.id);
        return;
      }
      // Support both channel ID and custom URL
      let url;
      if (result.custom_url) {
        url = `https://www.youtube.com/${result.custom_url}`;
      } else {
        url = `https://www.youtube.com/channel/${result.id}`;
      }
      if (onSearchChannelSelect) {
        onSearchChannelSelect(url);
      }
      if (onPageChange) {
        onPageChange('channel-analysis');
      }
    } else if (result.type === 'playlist') {
      if (!result.id || result.id === 'null' || result.id === 'undefined') {
        console.error('Invalid playlist ID:', result.id);
        return;
      }
      const url = `https://www.youtube.com/playlist?list=${result.id}`;
      if (onSearchVideoSelect) {
        onSearchVideoSelect(url);
      }
      if (onPageChange) {
        onPageChange('home');
      }
    }
    
    setSearchQuery('');
    setSearchResults(null);
    setShowSearchResults(false);
    
    ReactGA.event({
      category: 'Search',
      action: 'Result Clicked',
      label: result.type
    });
  };

  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.email) return user.email.split('@')[0];
    if (user?.username) return user.username;
    return 'User';
  };

  const getUserProfilePicture = () => {
    return user?.picture || null;
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Logo */}
        <div 
          onClick={() => onPageChange('home')}
          className="navbar-logo"
        >
          Gradi.AI
        </div>

        {/* Search Bar */}
        <div ref={searchInputRef} style={{ position: 'relative', flex: 1, maxWidth: '320px', margin: '0 1rem', zIndex: 1001, display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
          <input
            type="text"
            placeholder="Search YouTube..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSearchResults(false);
              setShowSuggestions(false);
            }}
            disabled={false}
            className="input-field"
            style={{
              width: '100%',
              minWidth: '200px',
              padding: '0.5rem 2rem 0.5rem 1rem',
              background: 'rgba(0, 0, 0, 0.7)',
              border: '2px solid transparent',
              borderRadius: '50px',
              color: 'white',
              fontSize: '0.85rem',
              fontWeight: '400',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(15px)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              fontFamily: 'inherit',
              outline: 'none',
              height: '32px',
              lineHeight: '32px'
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchQuery.trim().length >= 2) {
                setShowSuggestions(false);
                handleSearch(searchQuery);
              }
            }}
            onFocus={(e) => {
              // Show suggestions if typing, or full results if they exist
              if (searchQuery.trim().length >= 2) {
                if (searchSuggestions.length > 0) {
                  setShowSuggestions(true);
                } else if (searchResults) {
                  setShowSearchResults(true);
                }
              }
              if (e.currentTarget) {
                e.currentTarget.style.boxShadow = '0 0 30px rgba(139, 92, 246, 0.4), 0 0 60px rgba(236, 72, 153, 0.2)';
              }
            }}
            onBlur={(e) => {
              // Use ref instead of currentTarget to avoid null errors
              const inputElement = searchInputRef.current;
              if (inputElement) {
                setTimeout(() => {
                  if (inputElement && document.activeElement !== inputElement) {
                    inputElement.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
                  }
                }, 200);
              }
            }}
          />
          {isSearching && (
            <span className="spinner" style={{
              position: 'absolute',
              right: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.9rem'
            }} />
          )}
          {!isSearching && searchQuery && (
            <span 
              onClick={() => {
                setSearchQuery('');
                setSearchResults(null);
                setSearchSuggestions([]);
                setShowSearchResults(false);
                setShowSuggestions(false);
              }}
              style={{
                position: 'absolute',
                right: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#9ca3af',
                fontSize: '1.1rem',
                userSelect: 'none'
              }}
            >
              ×
            </span>
          )}

          <button
            onClick={handleFullSearchClick}
            disabled={isLaunchingSearchPage}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '999px',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              background: isLaunchingSearchPage ? 'rgba(15,15,35,0.6)' : 'rgba(15,15,35,0.85)',
              color: '#e5e7eb',
              cursor: isLaunchingSearchPage ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
            }}
            title="Open full search results"
          >
            {isLaunchingSearchPage ? (
              <span className="spinner" style={{ fontSize: '0.9rem' }} />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35m1.6-4.65a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </button>
          
          {/* Channel Suggestions Dropdown (YouTube-style autocomplete) */}
          {showSuggestions && searchSuggestions.length > 0 && (
            <div ref={suggestionsRef} className="dropdown-options" style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              zIndex: 10000,
              maxHeight: '400px',
              overflowY: 'auto',
              minWidth: '100%',
              backgroundColor: 'rgba(15, 15, 35, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '20px',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 30px rgba(139, 92, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              display: 'block',
              visibility: 'visible',
              opacity: 1
            }}>
              <div style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.85rem', fontWeight: '600', borderBottom: '1px solid rgba(139, 92, 246, 0.2)' }}>
                RESULTS
              </div>
                  {searchSuggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      className="dropdown-option"
                      onClick={() => {
                        if (!suggestion.id || suggestion.id === 'null' || suggestion.id === 'undefined') {
                          console.error('Invalid suggestion ID:', suggestion.id);
                          return;
                        }
                        // Support both channel ID and custom URL
                        let url;
                        if (suggestion.custom_url) {
                          url = `https://www.youtube.com/${suggestion.custom_url}`;
                        } else {
                          url = `https://www.youtube.com/channel/${suggestion.id}`;
                        }
                        if (onSearchChannelSelect) {
                          onSearchChannelSelect(url);
                        }
                        if (onPageChange) {
                          onPageChange('channel-analysis');
                        }
                        setSearchQuery('');
                        setSearchSuggestions([]);
                        setShowSuggestions(false);
                        
                        ReactGA.event({
                          category: 'Search',
                          action: 'Suggestion Clicked',
                          label: 'channel'
                        });
                      }}
                      style={{ cursor: 'pointer', padding: '1rem 1.5rem' }}
                    >
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%' }}>
                    {suggestion.thumbnail && (
                      <img src={suggestion.thumbnail} alt={suggestion.title} style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        flexShrink: 0
                      }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="option-text" style={{ fontSize: '1rem', fontWeight: '500', color: '#e5e7eb', marginBottom: '0.25rem' }}>
                        {suggestion.title}
                      </div>
                      {suggestion.custom_url && (
                        <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                          {suggestion.custom_url}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div style={{ 
                padding: '0.75rem 1rem', 
                borderTop: '1px solid rgba(139, 92, 246, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                  Show results for '{searchQuery}'
                </span>
                <button
                  onClick={() => {
                    setShowSuggestions(false);
                    handleSearch(searchQuery);
                  }}
                  style={{
                    background: 'rgba(139, 92, 246, 0.2)',
                    border: '1px solid rgba(139, 92, 246, 0.4)',
                    borderRadius: '8px',
                    color: '#8b5cf6',
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                  }}
                >
                  Enter
                </button>
              </div>
            </div>
          )}
          
          {/* Search Results Dropdown (Full results after Enter or clicking "Show results") */}
          {(showSearchResults || isSearching || searchResults) && !showSuggestions && (
            <div ref={searchResultsRef} className="dropdown-options" style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              zIndex: 10000,
              maxHeight: '400px',
              overflowY: 'auto',
              minWidth: '100%',
              backgroundColor: 'rgba(15, 15, 35, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '20px',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 30px rgba(139, 92, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              display: 'block',
              visibility: 'visible',
              opacity: 1
            }}>
              {isSearching ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                  <span className="spinner" /> Searching...
                </div>
              ) : searchResults && (
                <>
                  {searchResults.videos && searchResults.videos.length > 0 && (
                    <>
                      <div style={{ padding: '0.75rem 1rem', color: '#8b5cf6', fontSize: '0.85rem', fontWeight: '600', borderBottom: '1px solid rgba(139, 92, 246, 0.2)' }}>
                        Videos
                      </div>
                      {searchResults.videos.map((video) => (
                        <div
                          key={video.id}
                          className="dropdown-option"
                          onClick={() => handleSearchResultClick(video)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', width: '100%' }}>
                            {video.thumbnail && (
                              <img src={video.thumbnail} alt={video.title} style={{
                                width: '60px',
                                height: '45px',
                                borderRadius: '8px',
                                objectFit: 'cover'
                              }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="option-text" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                                {video.title}
                              </div>
                              <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                                {video.channel_title}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  
                  {searchResults.channels && searchResults.channels.length > 0 && (
                    <>
                      <div style={{ padding: '0.75rem 1rem', color: '#ec4899', fontSize: '0.85rem', fontWeight: '600', borderBottom: '1px solid rgba(236, 72, 153, 0.2)', borderTop: searchResults.videos && searchResults.videos.length > 0 ? '1px solid rgba(139, 92, 246, 0.2)' : 'none' }}>
                        Channels
                      </div>
                      {searchResults.channels.map((channel) => (
                        <div
                          key={channel.id}
                          className="dropdown-option"
                          onClick={() => handleSearchResultClick(channel)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', width: '100%' }}>
                            {channel.thumbnail && (
                              <img src={channel.thumbnail} alt={channel.title} style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                objectFit: 'cover'
                              }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="option-text" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                                {channel.title}
                              </div>
                              {channel.custom_url && (
                                <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                                  {channel.custom_url}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  
                  {searchResults.playlists && searchResults.playlists.length > 0 && (
                    <>
                      <div style={{ padding: '0.75rem 1rem', color: '#06b6d4', fontSize: '0.85rem', fontWeight: '600', borderBottom: '1px solid rgba(6, 182, 212, 0.2)', borderTop: ((searchResults.videos && searchResults.videos.length > 0) || (searchResults.channels && searchResults.channels.length > 0)) ? '1px solid rgba(236, 72, 153, 0.2)' : 'none' }}>
                        Playlists
                      </div>
                      {searchResults.playlists.map((playlist) => (
                        <div
                          key={playlist.id}
                          className="dropdown-option"
                          onClick={() => handleSearchResultClick(playlist)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', width: '100%' }}>
                            {playlist.thumbnail && (
                              <img src={playlist.thumbnail} alt={playlist.title} style={{
                                width: '60px',
                                height: '45px',
                                borderRadius: '8px',
                                objectFit: 'cover'
                              }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="option-text" style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                                {playlist.title}
                              </div>
                              <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                                {playlist.channel_title}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  
                  {(!searchResults.videos || searchResults.videos.length === 0) &&
                   (!searchResults.channels || searchResults.channels.length === 0) &&
                   (!searchResults.playlists || searchResults.playlists.length === 0) && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                      No results found
                    </div>
                  )}
                </>
              )}
              {!isSearching && !searchResults && searchQuery.trim().length >= 2 && (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                  Type to search...
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop Menu */}
        <div className="navbar-menu">
          {/* Menu Items */}
          <div className="navbar-items desktop-only">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onPageChange(item.id)}
                className={`navbar-item ${currentPage === item.id ? 'active' : ''}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* User Profile Dropdown */}
          <div className="navbar-user desktop-only" ref={profileDropdownRef}>
            {user ? (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    background: 'rgba(15, 15, 35, 0.95)',
                    border: '1px solid rgba(139, 92, 246, 0.5)',
                    borderRadius: '50px',
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    color: '#e5e7eb',
                    fontSize: '1rem',
                    fontWeight: '500',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.8)';
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.15)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                    e.currentTarget.style.background = 'rgba(15, 15, 35, 0.95)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
                  }}
                >
                  {getUserProfilePicture() ? (
                    <img 
                      src={getUserProfilePicture()} 
                      alt={getUserDisplayName()}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid rgba(139, 92, 246, 0.8)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      color: 'white',
                      border: '2px solid rgba(139, 92, 246, 0.8)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      {getUserDisplayName().charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span style={{ fontWeight: '500' }}>{getUserDisplayName()}</span>
                  <span style={{ 
                    fontSize: '0.8rem',
                    transition: 'transform 0.2s ease',
                    transform: isProfileDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    color: '#e5e7eb'
                  }}>
                    ▼
                  </span>
                </button>

                {/* Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'rgba(15, 15, 35, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '12px',
                    padding: '0.5rem',
                    minWidth: '200px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                    zIndex: 1000,
                    animation: 'fadeInDown 0.2s ease-out'
                  }}>
                    <button
                      onClick={() => {
                        onPageChange('dashboard');
                        setIsProfileDropdownOpen(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: currentPage === 'dashboard' ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: currentPage === 'dashboard' ? '#8b5cf6' : '#e5e7eb',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        marginBottom: '0.25rem'
                      }}
                      onMouseOver={(e) => {
                        if (currentPage !== 'dashboard') {
                          e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (currentPage !== 'dashboard') {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      Dashboard
                    </button>

                    {isAdmin && (
                      <button
                        onClick={() => {
                          onPageChange('admin');
                          setIsProfileDropdownOpen(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          background: currentPage === 'admin' ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          color: currentPage === 'admin' ? '#ef4444' : '#e5e7eb',
                          fontSize: '1rem',
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          marginBottom: '0.25rem'
                        }}
                        onMouseOver={(e) => {
                          if (currentPage !== 'admin') {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (currentPage !== 'admin') {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        🔐 Admin Dashboard
                      </button>
                    )}

                    <button
                      onClick={() => {
                        onLogout();
                        setIsProfileDropdownOpen(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#ef4444',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={onShowLogin}
                className="login-button-nav"
              >
                Login
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="navbar-mobile-toggle mobile-only"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            ☰
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="navbar-mobile-menu">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                onPageChange(item.id);
                setIsMobileMenuOpen(false);
              }}
              className={`navbar-mobile-item ${currentPage === item.id ? 'active' : ''}`}
            >
              {item.label}
            </button>
          ))}
          
          {/* Mobile User Info and Logout */}
          {user ? (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                borderTop: '1px solid rgba(139, 92, 246, 0.2)',
                marginTop: '0.5rem'
              }}>
                {getUserProfilePicture() ? (
                  <img 
                    src={getUserProfilePicture()} 
                    alt={getUserDisplayName()}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid rgba(139, 92, 246, 0.5)'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    {getUserDisplayName().charAt(0).toUpperCase()}
                  </div>
                )}
                <span style={{ color: '#e5e7eb', fontWeight: '500' }}>
                  {getUserDisplayName()}
                </span>
              </div>

              <button
                onClick={() => {
                  onPageChange('dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className={`navbar-mobile-item ${currentPage === 'dashboard' ? 'active' : ''}`}
              >
                Dashboard
              </button>

              {isAdmin && (
                <button
                  onClick={() => {
                    onPageChange('admin');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`navbar-mobile-item ${currentPage === 'admin' ? 'active' : ''}`}
                  style={{ color: currentPage === 'admin' ? '#ef4444' : undefined }}
                >
                  🔐 Admin Dashboard
                </button>
              )}

              <button
                onClick={() => {
                  onLogout();
                  setIsMobileMenuOpen(false);
                }}
                className="navbar-mobile-item logout-button"
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                onShowLogin();
                setIsMobileMenuOpen(false);
              }}
              className="navbar-mobile-item login-button-nav"
            >
              Login
            </button>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
